<template>
  <div id="secao-anexo">
    <h3 class="is-size-5 pl-2">
      Anexos:
      <span
        class="button is-loading is-white is-inline-block"
        style="vertical-align: bottom;"
        v-if="isAnexosLoading"
        data-label="loading-anexos"
      ></span>
    </h3>
    <div v-if="showUploadButton" class="container-adicionar-anexo">
      <strong class="pl-2">{{ addAnexoTitle }}</strong>
      <div :title="getTitleUpload">
        <TrfButtonUpLoadAttachment
          class="pl-2"
          data-cy="btn-adicionar-anexo"
          :tipLabel="tipLabelUpload"
          @uploadFile="adicionarAnexo"
          :disabled="isTarefaConcluida || isAnexosLoading"
        />
      </div>
    </div>
    
    <div v-if="anexos.length">
      <hr style="margin: 15px 0px 0px 0px"/>

      <div class="simple-table-anexos">
        <TrfSimpleTable
          :dados="anexos"
          :isCliente="false"
          :configuracaoDeColunas="configuracaoDeColunas"
        >             
          <template #nome="{ item }">
            <span data-label="nome-anexo" :title="item.nome">{{ item.nomeCurto }}</span>
          </template>
          <template #tamanho="{ item }">
            <span data-label="tamanho-anexo">{{ item.size }}</span>
          </template>
          <template #extensao="{ item }">
            <span data-label="extensao-anexo">{{ item.extensao }}</span>
          </template>
          <template #maisAcoes="{ item }">
            <button
              v-if="showDeleteButton"
              data-label="btn-excluir-anexo"
              data-cy="btn-excluir-anexo"
              class="button is-pulled-right ml-1"
              :title="getTitleExcluir"
              :disabled="isTarefaConcluida || !podeExcluirPorStatus || !hasPermissaoExcluir"
              @click="abrirModalExclusao(item)"
            >
              <span class="icon">
                <i class="mdi mdi-delete mdi-18px" aria-hidden="true"></i>
              </span>
            </button>
            <button
              data-label="btn-download-anexo"
              data-cy="btn-download-anexo"
              class="button is-pulled-right ml-1"
              title="Download"
              :class="{ 'is-loading': item.isDownloading }"
              @click="downloadAnexo(item)"
            >
              <span class="icon">
                <i class="mdi mdi-download mdi-18px" aria-hidden="true"></i>
              </span>
            </button>
            <button
              data-label="btn-visualizar-anexo"
              data-cy="btn-visualizar-anexo"
              class="button is-pulled-right"
              :title="item.permiteVisualizacao ? 'Visualizar' : 'A extensão do anexo não possui visualização.'"
              :disabled="!item.permiteVisualizacao"
              @click="visualizarAnexo(item)"
            >
              <span class="icon">
                <i class="mdi mdi-eye mdi-18px" aria-hidden="true"></i>
              </span>
            </button>
          </template>
        </TrfSimpleTable>
      </div>
    </div>
    <div v-else>
      <hr/>
      <h3 data-label="msg-sem-anexo" class="is-size-6 pl-2 has-text-grey">{{ mensagemSemAnexo }}</h3>
    </div>

    <TrfSimpleModal
      v-if="modalExclusao.isActive"
      data-cy="modal-excluir-anexo"
      titulo="Excluir anexo"
      :isAtivo="true"
      @close="cancelarModalExclusao"
    >
      <div class="msg-confirmar-exlusao p-4">
        Deseja excluir o anexo '<strong>{{ modalExclusao.anexo.nome + "." + modalExclusao.anexo.extensao}}</strong>'?
      </div>
      
      <template v-slot:slot-footer>
        <div>
          <button
            :class="{ 'button is-primary': true, 'is-loading': modalExclusao.isExcluindo }"
            data-cy="btn-confirmar-exclusao-anexo"
            @click="excluirAnexo"
          >Confirmar</button>
          <button
            class="button"
            data-cy="btn-cancelar-exclusao-anexo"
            @click="cancelarModalExclusao"
          >Cancelar</button>
        </div>
      </template>
    </TrfSimpleModal>
  </div>
</template>

<script>
import { defineAsyncComponent } from 'vue';
import { anexoService } from '../../service/anexo-service';
import { anexosAtendimentosService } from '../../service/anexos-atendimento-service';
import { toastUtils } from '../../util/toast.utils';
import { ToastBuilder, toastTypeEnum } from '../../builder/toast.builder';
import { pessoaLogada } from '../../stateManagement/pessoaLogada';
import { anexoMessagesEnum } from '../../enums/anexoMessagesEnum';
import { statusEnum } from '../../enums/statusEnum';
export default {
  name: 'TrfGestorAnexos',
  components: {
    TrfSimpleTable: defineAsyncComponent(() => import('../SimpleTable/index.vue')),
    TrfButtonUpLoadAttachment: defineAsyncComponent(() => import('../ButtonUpload/TrfButtonUpLoadAttachment')),
    TrfSimpleModal: defineAsyncComponent(() => import('../Modal/TrfSimpleModal.vue'))
  },
  props: {
    historicoId: {
      type: Number,
      required: true
    },
    faseId: {
      type: Number,
      default: null
    },
    atendimentoId: {
      type: Number,
      default: null
    },
    isTarefaConcluida: {
      type: Boolean,
      default: false
    },
    isTarefaDoLegalize: {
      type: Boolean,
      default: false
    },
    showUploadButton: {
      type: Boolean,
      default: true
    },
    showDeleteButton: {
      type: Boolean,
      default: true
    },
    statusId: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      isAnexosLoading: false,
      anexos: [],
      extensoes: {
        upload: ["bmp", "doc", "dot", "jpg", "pdf", "png", "xls", "odt", "odf", "rtf", "txt", "xml", "html", "gif", "zip", "rar", "xlsx", "docx", "ppt", "pptx", "7z", "jpeg", "htm", "csv", "qrp", "tdm", "ofx", "ofc"],
        visualizar: ['jpg', 'pdf', 'png', 'jpeg', 'bmp', 'gif', 'txt']
      },
      modalExclusao: {
        isExcluindo: false,
        isActive: false,
        anexo: null
      },
      configuracaoDeColunas: [
        { titulo: "Nome", nome: "nome", slot: true },
        { titulo: "Tamanho", nome: "tamanho", slot: true },
        { titulo: "Extensão", nome: "extensao", slot: true },
        { titulo: "", nome: "maisAcoes", customStyle: "z-index: 1; width: 130px;", slot: true }
      ]
    };
  },
  computed: {
    tipLabelUpload() {
      return `Extensões permitidas: *.${this.extensoes.upload.join(', *.')}.`;
    },
    getTitleUpload() {
      return this.isTarefaConcluida 
        ? 'Não é permitido adicionar anexos a tarefas e/ou fases que possuem o status Concluído.' 
        : '';
    },
    getTitleExcluir() {
      if (this.isTarefaConcluida) {
        return 'A exclusão de anexos não é permitida em tarefas e/ou fases com status Concluído.';
      }
      if (!this.podeExcluirPorStatus) {
        return 'A exclusão de anexos só é permitida em tarefas com status "A fazer" ou "Atrasada".';
      }
      if (!this.hasPermissaoExcluir) {
        return 'Você não possui permissão para excluir.';
      }
      return 'Excluir';
    },
    podeExcluirPorStatus() {
      if (!this.statusId) return true;
      return this.statusId === statusEnum.PENDENTE || this.statusId === statusEnum.ATRASADA;
    },
    hasPermissaoExcluir() {
      return pessoaLogada.hasPermissao("Tarefas", "Excluir");
    },
    addAnexoTitle() {
      const tipo = this.faseId ? 'fase' : 'tarefa';
      return anexoMessagesEnum.ADD_TITLE[tipo];
    },
    mensagemSemAnexo() {
      if (this.faseId) return anexoMessagesEnum.EMPTY.fase;
      if (this.isTarefaDoLegalize) return anexoMessagesEnum.EMPTY.processo;
      return anexoMessagesEnum.EMPTY.tarefa;
    }
  },
  methods: {
    async carregarAnexos() {
      this.isAnexosLoading = true;
      const anexosDto = await anexoService.obterAnexos(this.historicoId, this.faseId, this.atendimentoId);
      this.anexos = anexosDto.map(dto => this.getAnexoFromDto(dto));
      this.isAnexosLoading = false;
    },
    getAnexoFromDto(anexoDto) {
      const [nome, extensao] = anexoDto.isAnexoDeAtendimento 
        ? [anexoDto.nome, anexoDto.extensao] 
        : this.splitNomeExtensao(anexoDto.nome);
      const nomeCurto = this.getSubstring(nome, 20);
      const permiteVisualizacao = this.extensoes.visualizar.includes(extensao);
      
      return {
        id: anexoDto.id,
        nome,
        nomeCurto,
        nomeCompleto: anexoDto.nome,
        type: anexoDto.mime,
        size: (anexoDto.size / (1024 * 1024)).toFixed(2) + "MB",
        extensao: anexoDto.extensao,
        permiteVisualizacao,
        isDownloading: false,
        urlS3Download: anexoDto.urlS3Download,
        urlS3Visualizar: anexoDto.urlS3Visualizar,
        isAnexoDeAtendimento: anexoDto.isAnexoDeAtendimento
      };
    },
    getSubstring(texto, size) {
      const textoCortado = texto.substring(0, size);
      return texto.length > size ? textoCortado + "..." : textoCortado;
    },
    visualizarAnexo(anexo) {
      window.open(anexo.urlS3Visualizar, '_blank');
    },
    downloadAnexo(anexo) {
      anexo.isDownloading = true;
      const link = document.createElement('a');
      link.href = anexo.urlS3Download;
      link.download = anexo.nome;
      link.click();
      anexo.isDownloading = false;
    },
    abrirModalExclusao(anexo) {
      this.modalExclusao.isActive = true;
      this.modalExclusao.anexo = anexo;
    },
    cancelarModalExclusao() {
      this.modalExclusao.isActive = false;
      this.modalExclusao.anexo = null;
    },
    async excluirAnexo() {
      this.modalExclusao.isExcluindo = true;

      try {
        let response;
        if (this.modalExclusao.anexo.isAnexoDeAtendimento) {
          response = await anexosAtendimentosService.excluirAnexo(this.modalExclusao.anexo);
        } else {
          response = await anexoService.excluirAnexo(this.modalExclusao.anexo);
        }

        if (response.status === 200 || response.status === 204) {
          await this.carregarAnexos();
          toastUtils.successToast("Anexo excluído com sucesso!");
          this.$emit('anexos-alterados');
        } else {
          this.dangerToast("Ocorreu um erro durante a exclusão! Tente novamente mais tarde.");
        }
      } catch (error) {
        console.error('Erro ao excluir anexo:', error);
        this.dangerToast("Ocorreu um erro durante a exclusão! Tente novamente mais tarde.");
      } finally {
        this.modalExclusao.isExcluindo = false;
        this.modalExclusao.isActive = false;
      }
    },
    async adicionarAnexo(event) {
      this.isAnexosLoading = true;

      const files = event.target.files;
      const novosAnexos = [];
      const errors = [];

      for (const arquivo of Array.from(files)) {

        if (arquivo.size > 50 * 1024 * 1024) {
          errors.push("Arquivo '" + arquivo.name + "' excede o limite máximo permitido de 50 MB!");
          continue;
        }

        let base64 = await this.converterArquivoParaBase64(arquivo);
        base64 = base64.split(",")[1];

        const [, extensao] = this.splitNomeExtensao(arquivo.name);

        let anexo = {
          nome: arquivo.name,
          extensao,
          mime: arquivo.type,
          size: arquivo.size,
          bytes: base64,
        };
        
        novosAnexos.push(anexo);
      }

      if (!novosAnexos.length) {
        this.isAnexosLoading = false;
        errors.forEach(e => this.dangerToast(e));
        return;
      }

      try {
        const response = await anexoService.incluirAnexos(this.historicoId, this.faseId, novosAnexos);
          
        if (response.status !== 200) {
          this.dangerToast("Ocorreu um erro durante a inclusão! Tente novamente mais tarde.");
          this.isAnexosLoading = false;
          return;
        }

        const json = await response.json();
        if (json.totalAdicionados != 0) {
          await this.carregarAnexos();
          toastUtils.successToast("Anexo(s) incluído(s) com sucesso!");
          this.$emit('anexos-alterados');
        }
        
        errors.concat(json.errors).forEach(e => this.dangerToast(e));
      } catch (error) {
        console.error('Erro ao adicionar anexo:', error);
        this.dangerToast("Ocorreu um erro durante a inclusão! Tente novamente mais tarde.");
      } finally {
        this.isAnexosLoading = false;
      }
    },
    async converterArquivoParaBase64(arquivo) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = (error) => reject(error);
        reader.readAsDataURL(arquivo);
      });
    },
    splitNomeExtensao(nomeCompleto) {
      const nomeArray = nomeCompleto.split('.');
      const extensao = nomeArray.pop().toLowerCase();
      const nome = nomeArray.join('.');
      return [nome, extensao];
    },
    dangerToast(message) {
      new ToastBuilder()
        .setType(toastTypeEnum.DANGER)
        .setDuration(5000)
        .setMessage(message)
        .toast();
    }
  },
  mounted() {
    this.carregarAnexos();
  }
};
</script>

<style lang="scss" scoped>
#secao-anexo {
  margin-top: 0px;
}

.container-adicionar-anexo {
  margin-top: 5px;
}

.msg-confirmar-exlusao {
  padding-left: 35px !important;
}

.simple-table-anexos {
  overflow-y: auto;
  max-height: 200px;
  scrollbar-width: thin;
  scrollbar-color: #969696 #dbdbdb;
}
</style>
